import React, { useState } from 'react';

import { Search } from '@videoblocks/react-icons';

import useMedia from '../../common/hooks/useMedia';
import TopNavSearchBar from './TopNavSearchBar';
import DesktopNavActions from './desktop/DesktopNavActions';
import DesktopNavItem from './desktop/DesktopNavItem';
import MobileNav from './mobile/MobileNav';
import TabletNavActions from './tablet/TabletNavActions';
import type {
  DesktopNavConfig,
  MobileNavConfig,
  TabletNavConfig,
} from './types';

interface TopNavigationBarProps {
  desktop: DesktopNavConfig;
  tablet: TabletNavConfig;
  mobile: MobileNavConfig;
  logoUrl: string;
  showSearch?: boolean;
}

export default function TopNavigationBar({
  desktop,
  tablet,
  mobile,
  logoUrl,
  showSearch = false,
}: TopNavigationBarProps): JSX.Element {
  const [isSearchExpanded, setIsSearchExpanded] = useState(showSearch);
  const isDesktopXl = useMedia('(min-width: 1280px)', false);
  const showSearchBar = isDesktopXl ? isSearchExpanded : true;

  return (
    <nav className="sticky top-0 z-50 w-full border-b border-gray-200 bg-white">
      <div className="flex items-center justify-between p-4 topnav-xl:px-6 topnav-xl:py-4">
        <div className="flex items-center gap-5">
          <a href="/" className="flex-shrink-0">
            <img src={logoUrl} alt="Storyblocks" width={151} height={32} />
          </a>
          {/* Tablet inline items (lg to xl) */}
          <div className="hidden items-center lg:flex topnav-xl:hidden">
            {tablet.navItems.map((item) => (
              <DesktopNavItem key={item.id} config={item} />
            ))}
          </div>
          {/* Desktop inline items (xl+) */}
          <div className="hidden items-center topnav-xl:flex">
            {desktop.navItems.map((item) => (
              <DesktopNavItem key={item.id} config={item} />
            ))}
            {!isSearchExpanded && (
              <button
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                aria-label="Open search"
                className="ml-2 p-2 text-gray-600 hover:text-gray-900"
              >
                <Search className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center gap-4">
          <DesktopNavActions items={desktop.actions} />
          <TabletNavActions config={tablet} logoUrl={logoUrl} />
          <MobileNav config={mobile} logoUrl={logoUrl} />
        </div>
      </div>

      {showSearchBar && (
        <div
          className={`flex justify-center pb-2 ${
            isDesktopXl ? 'px-6' : 'px-4'
          }`}
        >
          <TopNavSearchBar
            onCollapse={
              isDesktopXl ? () => setIsSearchExpanded(false) : () => {}
            }
            showXButton={isDesktopXl}
          />
        </div>
      )}
    </nav>
  );
}
